import DOMPurify from 'dompurify';

const HTML_TAG_PATTERN = /<[a-z][\s\S]*>/i;

const ALLOWED_IFRAME_HOSTS = [
  'youtube.com',
  'www.youtube.com',
  'youtube-nocookie.com',
  'www.youtube-nocookie.com',
  'youtu.be',
  'vimeo.com',
  'player.vimeo.com',
];

const ALLOWED_IFRAME_PATH_PATTERN = /\.mp4(\?|$)/i;

/** font-family style 값 허용 목록 (소문자 비교) */
const ALLOWED_FONT_FAMILY_FRAGMENTS = [
  'pretendard',
  'noto sans kr',
  'noto sans jp',
  'noto sans thai',
  'noto sans khmer',
  'noto sans arabic',
  'noto sans',
  'nanum gothic',
  'nanum myeongjo',
  'nanum pen script',
  'apple sd gothic neo',
  'malgun gothic',
  'segoe ui',
  'arial',
  'sans-serif',
  // legacy
  '맑은 고딕',
  'dotum',
  '돋움',
  'gulim',
  '굴림',
  'batang',
  '바탕',
  'gungsuh',
  '궁서',
];

const isAllowedFontFamilyValue = (raw: string): boolean => {
  const normalized = raw.toLowerCase().replace(/['"]/g, '').trim();
  if (!normalized) return false;
  return ALLOWED_FONT_FAMILY_FRAGMENTS.some(fragment =>
    normalized.includes(fragment),
  );
};

const filterAllowedInlineStyles = (styleValue: string): string => {
  const allowed = styleValue
    .split(';')
    .map(part => part.trim())
    .filter(part => {
      if (/^color\s*:/i.test(part)) return true;
      if (/^font-family\s*:/i.test(part)) {
        const value = part.replace(/^font-family\s*:/i, '').trim();
        return isAllowedFontFamilyValue(value);
      }
      return false;
    });

  return allowed.join('; ');
};

export const normalizePlainText = (raw: string): string =>
  raw.replace(/\r\n/g, '\n').replace(/\\n/g, '\n');

export const isRichHtml = (raw: string): boolean =>
  HTML_TAG_PATTERN.test(raw.trim());

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

export const plainTextToQuillHtml = (raw: string): string => {
  const normalized = normalizePlainText(raw);
  if (!normalized.trim()) return '';

  return normalized
    .split('\n')
    .map(line => {
      if (!line) return '<p><br></p>';
      return `<p>${escapeHtml(line)}</p>`;
    })
    .join('');
};

const convertVideoLinksToIframes = (html: string): string => {
  if (!html.includes('<a') || typeof document === 'undefined') {
    return html;
  }

  const template = document.createElement('template');
  template.innerHTML = html;

  template.content.querySelectorAll('a').forEach(anchor => {
    const href = anchor.getAttribute('href')?.trim() ?? '';
    const embedUrl = normalizeVideoEmbedUrl(href) ?? href;

    if (!isAllowedIframeSrc(embedUrl)) return;

    const iframe = document.createElement('iframe');
    iframe.setAttribute('src', embedUrl);
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('allowfullscreen', 'true');
    iframe.className = 'ql-video';
    anchor.replaceWith(iframe);
  });

  return template.innerHTML;
};

export const toQuillHtml = (raw: string): string => {
  const normalized = normalizePlainText(raw ?? '');
  if (!normalized.trim()) return '';

  const html = isRichHtml(normalized)
    ? normalized
    : plainTextToQuillHtml(normalized);

  return convertVideoLinksToIframes(html);
};

export const prepareMedicalNoteHtmlForDisplay = (html: string): string => {
  if (!html.trim()) return '';
  return sanitizeMedicalNoteHtml(convertVideoLinksToIframes(html));
};

const isAllowedIframeSrc = (src: string): boolean => {
  if (!src) return false;

  try {
    const url = new URL(src, window.location.origin);

    if (url.protocol !== 'https:' && url.protocol !== 'http:') {
      return false;
    }

    const host = url.hostname.toLowerCase();
    if (
      ALLOWED_IFRAME_HOSTS.some(
        allowed => host === allowed || host.endsWith(`.${allowed}`),
      )
    ) {
      return true;
    }

    return ALLOWED_IFRAME_PATH_PATTERN.test(url.pathname);
  } catch {
    return false;
  }
};

const isAllowedImageSrc = (src: string): boolean => {
  if (!src) return false;
  if (src.startsWith('data:')) return false;

  try {
    const url = new URL(src, window.location.origin);
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch {
    return false;
  }
};

let sanitizeHookRegistered = false;

const registerSanitizeHooks = () => {
  if (sanitizeHookRegistered || typeof window === 'undefined') return;
  sanitizeHookRegistered = true;

  DOMPurify.addHook('uponSanitizeAttribute', (_node, data) => {
    if (data.attrName !== 'style') return;

    const allowedStyles = filterAllowedInlineStyles(data.attrValue);

    if (!allowedStyles) {
      data.keepAttr = false;
      return;
    }

    data.attrValue = allowedStyles;
  });

  DOMPurify.addHook('uponSanitizeElement', (node, data) => {
    if (!(node instanceof Element)) return;

    if (data.tagName === 'iframe') {
      const src = node.getAttribute('src') ?? '';
      if (!isAllowedIframeSrc(src)) {
        node.remove();
      }
    }

    if (data.tagName === 'img') {
      const src = node.getAttribute('src') ?? '';
      if (!isAllowedImageSrc(src)) {
        node.remove();
      }
    }
  });
};

registerSanitizeHooks();

export const sanitizeMedicalNoteHtml = (html: string): string => {
  if (!html.trim()) return '';

  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'p',
      'br',
      'strong',
      'b',
      'em',
      'i',
      'u',
      'h1',
      'h2',
      'ol',
      'ul',
      'li',
      'img',
      'iframe',
      'hr',
      'span',
    ],
    ALLOWED_ATTR: [
      'src',
      'alt',
      'class',
      'style',
      'frameborder',
      'allowfullscreen',
      'allow',
      'data-list',
    ],
  });
};

export const normalizeVideoEmbedUrl = (rawUrl: string): string | null => {
  const trimmed = rawUrl.trim();
  if (!trimmed) return null;

  try {
    const url = new URL(trimmed);

    if (url.hostname.includes('youtu.be')) {
      const videoId = url.pathname.replace('/', '');
      return videoId
        ? `https://www.youtube.com/embed/${videoId}`
        : null;
    }

    if (url.hostname.includes('youtube.com')) {
      const videoId = url.searchParams.get('v');
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}`;
      }

      if (url.pathname.startsWith('/embed/')) {
        return trimmed;
      }
    }

    if (url.hostname.includes('vimeo.com')) {
      const segments = url.pathname.split('/').filter(Boolean);
      const videoId = segments[segments.length - 1];
      return videoId ? `https://player.vimeo.com/video/${videoId}` : null;
    }

    if (ALLOWED_IFRAME_PATH_PATTERN.test(url.pathname)) {
      return trimmed;
    }

    return null;
  } catch {
    return null;
  }
};

export const isEmptyMedicalNote = (raw: string): boolean =>
  !normalizePlainText(raw).trim();
